import {
  Card,
  Branch,
  getCardCost,
  getForwardCost,
  getAddCost,
  getMultiplyCost,
  CARD_DEFINITIONS,
  FORWARD_ORB_COST,
  BRANCH_COST,
  WHILE_BASE_COST,
  TURN_VALUE_TO_DIRECTION,
  TURN_DIRECTION_VALUES,
  TURN_DIRECTION_LABELS,
  SUBBOOK_LABEL_SUFFIX,
  type TurnDirection,
  type SubbookId,
} from "@/program/Card";
import {
  getConditionLabel,
  OPERATOR_LABELS,
  type Condition,
} from "@/program/Condition";
import { OrbColor, ORB_COLOR_LABELS } from "@/program/VariableState";
import { StepSelectorModal, StepSelection } from "./StepSelectorModal";
import { OrbSelectorModal, OrbCardMode } from "./OrbSelectorModal";
import { ConditionSelectorModal } from "./ConditionSelectorModal";
import { TurnSelectorModal } from "./TurnSelectorModal";
import { SubbookSelectorModal } from "./SubbookSelectorModal";
import { getCardColor } from "./theme";
import {
  getTurnDirectionSVG,
  getWhileIconSVG,
  getCallIconSVG,
} from "./turnIconSVG";

/** パレットD&D用 MIME タイプ（内部移動用の mimeType と分離して外部ドロップを防ぐ） */
export const PALETTE_MIME_TYPE = "application/x-questile-palette";

export interface CardListEngineOptions {
  /** DnD用MIMEタイプ（コンテナ間分離） */
  mimeType: string;
  /** ルートドロップコンテナ（dragover/drop先・clearDropIndicatorsの範囲） */
  rootDropContainer: HTMLElement;
  /** while/turnカードにSVGアイコンを使うか */
  useIconSVG?: boolean;
  /** カード変更後フック（render後に毎回呼ばれる） */
  onCardsChanged?: () => void;
  /** ドロップ完了フック（例: ProgramAreaのflashDroppedCard） */
  onDropComplete?: (cardId: string) => void;
  /** モーダルに渡す利用可能MP（省略時はInfinity） */
  getAvailableMP?: (card: Card) => number;
  /** addElseBranchのMP上限取得（省略時はInfinity） */
  getMaxMP?: () => number;
  /** render末尾に追加する要素（例: ProgramAreaのdrop-tail、SubbookPanelの追加ボタン） */
  renderSuffix?: () => HTMLElement[];
  /** ブランチボディ内の「呪文を追加」ボタンクリック時フック（SubbookPanel用: activate呼び出し） */
  onBranchBodyAddClicked?: (branchId: string) => void;
  /** プレースホルダーを表示するか（デフォルト: cards.length === 0） */
  shouldShowPlaceholder?: () => boolean;
  /** プレースホルダーテキスト（デフォルト: ""） */
  placeholderText?: string;
  /** パレットからブランチへD&Dされたときのコールバック（branchId, cardType, 挿入インデックス） */
  onPaletteDropToBranch?: (
    branchId: string,
    cardType: string,
    insertionIndex?: number
  ) => void;
}

const MAX_RENDER_DEPTH = 20;

/**
 * カードリスト管理エンジン
 *
 * ProgramArea / SubbookPanel 共通のカード操作・レンダリング・D&Dロジックを集約する。
 * 両クラスはこのEngineをフィールドに持ち、各固有ロジック（MP管理・アクティブ化等）のみ実装する。
 */
export class CardListEngine {
  readonly cards: Card[] = [];

  private activeContainerId: string | null = null;
  private draggingCardId: string | null = null;
  private dragPreview: HTMLElement | null = null;
  private turnModal?: TurnSelectorModal;
  private subbookModal?: SubbookSelectorModal;
  private subbookSlots: number = 3;

  private readonly opts: CardListEngineOptions;

  constructor(opts: CardListEngineOptions) {
    this.opts = opts;
    this.setupRootDropEvents();
  }

  // ─── カード操作（public） ───

  addCardToRoot(card: Card, insertionIndex?: number): void {
    if (insertionIndex !== undefined && insertionIndex < this.cards.length) {
      this.cards.splice(insertionIndex, 0, card);
    } else {
      this.cards.push(card);
    }
    this.render();
    this.opts.onCardsChanged?.();
  }

  addCardToBranch(branchId: string, card: Card, insertionIndex?: number): void {
    const branch = this.findBranch(branchId);
    if (branch) {
      if (
        insertionIndex !== undefined &&
        insertionIndex < branch.cards.length
      ) {
        branch.cards.splice(insertionIndex, 0, card);
      } else {
        branch.cards.push(card);
      }
    } else {
      // 無効なブランチ → ルートにフォールバック
      this.cards.push(card);
    }
    this.render();
    this.opts.onCardsChanged?.();
  }

  removeCard(cardId: string): void {
    // 削除対象がif/whileカードでそのブランチがアクティブなら解除
    if (this.activeContainerId) {
      const cardToDelete = this.findCard(cardId);
      if (
        cardToDelete?.branches?.some((b) => b.id === this.activeContainerId)
      ) {
        this.activeContainerId = null;
      }
    }

    if (this.removeCardFromList(this.cards, cardId)) {
      this.render();
      this.opts.onCardsChanged?.();
    }
  }

  clear(): void {
    this.cards.splice(0, this.cards.length);
    this.activeContainerId = null;
    this.render();
    this.opts.onCardsChanged?.();
  }

  getCards(): Card[] {
    return [...this.cards];
  }

  getTotalCost(): number {
    return this.cards.reduce((sum, card) => sum + getCardCost(card), 0);
  }

  // ─── ターゲットモード ───

  setActiveContainerId(id: string | null): void {
    this.activeContainerId = id;
  }

  getActiveContainerId(): string | null {
    return this.activeContainerId;
  }

  // ─── カードハイライト ───

  highlightCard(cardId: string): void {
    this.clearHighlight();
    const element = this.opts.rootDropContainer.querySelector(
      `[data-card-id="${CSS.escape(cardId)}"]`
    ) as HTMLElement | null;
    if (element) {
      element.classList.add("card-d--executing");
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      element.scrollIntoView({
        block: "nearest",
        behavior: prefersReducedMotion ? "auto" : "smooth",
      });
    }
  }

  clearHighlight(): void {
    this.opts.rootDropContainer
      .querySelectorAll(".card-d--executing")
      .forEach((el) => el.classList.remove("card-d--executing"));
  }

  // ─── ツリー検索 ───

  findCard(cardId: string, cards: Card[] = this.cards): Card | undefined {
    for (const card of cards) {
      if (card.id === cardId) return card;
      if (card.branches) {
        for (const branch of card.branches) {
          const found = this.findCard(cardId, branch.cards);
          if (found) return found;
        }
      }
    }
    return undefined;
  }

  findBranch(branchId: string, cards: Card[] = this.cards): Branch | undefined {
    for (const card of cards) {
      if (card.branches) {
        for (const branch of card.branches) {
          if (branch.id === branchId) return branch;
          const found = this.findBranch(branchId, branch.cards);
          if (found) return found;
        }
      }
    }
    return undefined;
  }

  findContainerOf(
    cardId: string,
    cards: Card[] = this.cards
  ): Card[] | undefined {
    if (cards.some((c) => c.id === cardId)) return cards;
    for (const card of cards) {
      if (card.branches) {
        for (const branch of card.branches) {
          const found = this.findContainerOf(cardId, branch.cards);
          if (found) return found;
        }
      }
    }
    return undefined;
  }

  private removeCardFromList(cards: Card[], cardId: string): boolean {
    const index = cards.findIndex((c) => c.id === cardId);
    if (index !== -1) {
      cards.splice(index, 1);
      return true;
    }
    for (const card of cards) {
      if (card.branches) {
        for (const branch of card.branches) {
          if (this.removeCardFromList(branch.cards, cardId)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // ─── 循環防止チェック ───

  isDescendantOf(cardId: string, ancestorId: string): boolean {
    const ancestor = this.findCard(ancestorId);
    if (!ancestor?.branches) return false;
    for (const branch of ancestor.branches) {
      for (const child of branch.cards) {
        if (child.id === cardId) return true;
        if (child.branches && this.isDescendantOf(cardId, child.id))
          return true;
      }
    }
    return false;
  }

  isBranchDescendantOf(branchId: string, cardId: string): boolean {
    const card = this.findCard(cardId);
    if (!card?.branches) return false;
    for (const branch of card.branches) {
      if (branch.id === branchId) return true;
      for (const child of branch.cards) {
        if (this.isBranchDescendantOf(branchId, child.id)) return true;
      }
    }
    return false;
  }

  // ─── カード移動 ───

  moveCardUp(cardId: string): void {
    const container = this.findContainerOf(cardId);
    if (!container) return;
    const index = container.findIndex((c) => c.id === cardId);
    if (index <= 0) return;
    [container[index - 1], container[index]] = [
      container[index],
      container[index - 1],
    ];
    this.render();
    this.opts.onCardsChanged?.();
  }

  moveCardDown(cardId: string): void {
    const container = this.findContainerOf(cardId);
    if (!container) return;
    const index = container.findIndex((c) => c.id === cardId);
    if (index === -1 || index >= container.length - 1) return;
    [container[index], container[index + 1]] = [
      container[index + 1],
      container[index],
    ];
    this.render();
    this.opts.onCardsChanged?.();
  }

  moveCard(
    draggedId: string,
    targetId: string,
    position: "before" | "after"
  ): void {
    if (draggedId === targetId) return;
    if (this.isDescendantOf(targetId, draggedId)) return;

    const draggedContainer = this.findContainerOf(draggedId);
    const targetContainer = this.findContainerOf(targetId);
    if (!draggedContainer || !targetContainer) return;

    const draggedIndex = draggedContainer.findIndex((c) => c.id === draggedId);
    if (draggedIndex === -1) return;
    const [draggedCard] = draggedContainer.splice(draggedIndex, 1);

    let targetIndex = targetContainer.findIndex((c) => c.id === targetId);
    if (targetIndex === -1) {
      draggedContainer.splice(draggedIndex, 0, draggedCard);
      this.render();
      return;
    }

    if (position === "after") targetIndex++;
    targetContainer.splice(targetIndex, 0, draggedCard);

    this.render();
    this.opts.onDropComplete?.(draggedId);
    this.opts.onCardsChanged?.();
  }

  moveCardToBranch(draggedId: string, branchId: string, index?: number): void {
    if (this.isBranchDescendantOf(branchId, draggedId)) return;

    const branch = this.findBranch(branchId);
    if (!branch) return;

    const container = this.findContainerOf(draggedId);
    if (!container) return;

    const idx = container.findIndex((c) => c.id === draggedId);
    if (idx === -1) return;

    const [card] = container.splice(idx, 1);

    if (index !== undefined) {
      const adjusted =
        container === branch.cards && idx < index ? index - 1 : index;
      branch.cards.splice(adjusted, 0, card);
    } else {
      branch.cards.push(card);
    }

    this.render();
    this.opts.onDropComplete?.(draggedId);
    this.opts.onCardsChanged?.();
  }

  moveCardToRoot(draggedId: string, index?: number): void {
    const container = this.findContainerOf(draggedId);
    if (!container) return;

    if (container === this.cards) {
      if (index === undefined) return;
      const idx = container.findIndex((c) => c.id === draggedId);
      if (idx === -1) return;
      const [card] = container.splice(idx, 1);
      this.cards.splice(idx < index ? index - 1 : index, 0, card);
      this.render();
      this.opts.onDropComplete?.(draggedId);
      this.opts.onCardsChanged?.();
      return;
    }

    const idx = container.findIndex((c) => c.id === draggedId);
    if (idx === -1) return;

    const [card] = container.splice(idx, 1);
    if (index !== undefined) {
      this.cards.splice(index, 0, card);
    } else {
      this.cards.push(card);
    }

    this.render();
    this.opts.onDropComplete?.(draggedId);
    this.opts.onCardsChanged?.();
  }

  // ─── カード更新 ───

  updateForwardCard(cardId: string, selection: StepSelection): void {
    const card = this.findCard(cardId);
    if (!card || card.type !== "forward") return;

    if (selection.type === "steps") {
      card.value = selection.value;
      card.orbColor = undefined;
      card.cost = getForwardCost(selection.value);
    } else {
      card.value = undefined;
      card.orbColor = selection.color;
      card.cost = FORWARD_ORB_COST;
    }

    this.render();
    this.opts.onCardsChanged?.();
  }

  updateOrbCard(
    cardId: string,
    operator: OrbCardMode,
    color: OrbColor,
    value: number
  ): void {
    const card = this.findCard(cardId);
    if (!card) return;

    card.type = operator === "add" ? "addToOrb" : "multiplyOrb";
    card.label = CARD_DEFINITIONS[card.type].label;
    card.orbColor = color;
    card.value = value;
    card.cost = operator === "add" ? getAddCost(value) : getMultiplyCost(value);

    this.render();
    this.opts.onCardsChanged?.();
  }

  updateTurnCard(cardId: string, direction: TurnDirection): void {
    const card = this.findCard(cardId);
    if (!card || card.type !== "turn") return;

    card.value = TURN_DIRECTION_VALUES[direction];

    this.render();
    this.opts.onCardsChanged?.();
  }

  updateCallCard(cardId: string, subbookId: SubbookId): void {
    const card = this.findCard(cardId);
    if (!card || card.type !== "call") return;

    card.subbookId = subbookId;
    card.label = `よびだす ${SUBBOOK_LABEL_SUFFIX[subbookId]}`;

    this.render();
    this.opts.onCardsChanged?.();
  }

  setSubbookSlots(slots: number): void {
    this.subbookSlots = slots;
  }

  updateBranchCondition(branchId: string, condition: Condition): void {
    const branch = this.findBranch(branchId);
    if (!branch) return;

    branch.condition = condition;
    this.render();
    this.opts.onCardsChanged?.();
  }

  addElseBranch(ifCard: Card, maxMP: number = Infinity): void {
    if (!ifCard.branches) return;
    if (ifCard.branches.some((b) => !b.condition)) return;

    if (this.getTotalCost() + BRANCH_COST > maxMP) return;

    const newBranch: Branch = {
      id: crypto.randomUUID(),
      cards: [],
    };

    ifCard.branches.push(newBranch);

    this.render();
    this.opts.onCardsChanged?.();
  }

  removeBranch(ifCardId: string, branchId: string): void {
    const ifCard = this.findCard(ifCardId);
    if (!ifCard || !ifCard.branches) return;

    const index = ifCard.branches.findIndex((b) => b.id === branchId);
    if (index <= 0) return;

    ifCard.branches.splice(index, 1);

    if (this.activeContainerId === branchId) {
      this.activeContainerId = null;
    }

    this.render();
    this.opts.onCardsChanged?.();
  }

  // ─── レンダリング ───

  render(): void {
    const showPlaceholder = this.opts.shouldShowPlaceholder
      ? this.opts.shouldShowPlaceholder()
      : this.cards.length === 0;

    const suffix = this.opts.renderSuffix?.() ?? [];

    if (showPlaceholder) {
      const placeholder = document.createElement("div");
      placeholder.className = "program-placeholder";
      placeholder.textContent = this.opts.placeholderText ?? "";
      this.opts.rootDropContainer.replaceChildren(placeholder, ...suffix);
    } else {
      const counter = { value: 0 };
      const elements = this.renderCardList(this.cards, 0, counter);

      const rootNest = document.createElement("div");
      rootNest.className = "nest nest--root";
      rootNest.style.setProperty("--c", "var(--warm-500)");
      const rootAdd = document.createElement("div");
      rootAdd.className = "nest__add";
      if (this.activeContainerId === null) {
        rootAdd.classList.add("nest__add--target");
      }
      rootAdd.textContent = "＋ 呪文を追加";
      rootAdd.addEventListener("click", (e) => {
        e.stopPropagation();
        this.setActiveContainerId(null);
        this.render();
      });
      rootNest.appendChild(rootAdd);
      elements.push(rootNest);

      this.opts.rootDropContainer.replaceChildren(...elements, ...suffix);
    }
  }

  private renderCardList(
    cards: Card[],
    depth: number,
    counter: { value: number }
  ): HTMLElement[] {
    if (depth > MAX_RENDER_DEPTH) return [];

    const elements: HTMLElement[] = [];

    for (const card of cards) {
      counter.value++;

      if (card.type === "if") {
        const header = this.createCardElement(card, counter.value);
        elements.push(header);
        const branchElements = this.createIfBranchElements(
          card,
          depth,
          counter
        );
        elements.push(...branchElements);
      } else if (card.type === "while") {
        const header = this.createCardElement(card, counter.value);
        elements.push(header);
        const branch = card.branches?.[0];
        if (branch) {
          const body = this.createWhileBodyElement(branch, depth, counter);
          elements.push(body);
        }
      } else {
        const element = this.createCardElement(card, counter.value);
        elements.push(element);
      }
    }

    return elements;
  }

  private createIfBranchElements(
    card: Card,
    depth: number,
    counter: { value: number }
  ): HTMLElement[] {
    const elements: HTMLElement[] = [];
    const branches = card.branches ?? [];

    for (let i = 0; i < branches.length; i++) {
      const branch = branches[i];

      if (i > 0) {
        const branchHeader = this.createBranchHeaderElement(card, branch);
        elements.push(branchHeader);
      }

      const body = this.createBranchBodyElement(branch, depth, counter);
      elements.push(body);
    }

    const hasElse = branches.some((b) => !b.condition);
    if (!hasElse) {
      const addBranchRow = this.createAddBranchButtons(card);
      elements.push(addBranchRow);
    }

    return elements;
  }

  private createBranchHeaderElement(ifCard: Card, branch: Branch): HTMLElement {
    const elseCard = document.createElement("div");
    elseCard.className = "card-d card-d--else";
    elseCard.style.setProperty("--c", getCardColor("if"));
    elseCard.setAttribute("data-branch-id", branch.id);

    const elseStripe = document.createElement("div");
    elseStripe.className = "card-d__stripe";
    elseStripe.style.background = "var(--c)";
    elseCard.appendChild(elseStripe);

    const elseIcon = document.createElement("span");
    elseIcon.className = "card-d__icon";
    elseIcon.setAttribute("aria-hidden", "true");
    elseIcon.textContent = "?";
    elseCard.appendChild(elseIcon);

    const elseLabel = document.createElement("span");
    elseLabel.className = "card-d__label";
    elseLabel.textContent = "それ以外";
    elseCard.appendChild(elseLabel);

    const elseActions = document.createElement("span");
    elseActions.className = "card-d__actions";
    const elseRemoveBtn = document.createElement("button");
    elseRemoveBtn.className = "card-d__btn card-d__btn--del";
    elseRemoveBtn.setAttribute("aria-label", "それ以外を削除");
    elseRemoveBtn.textContent = "×";
    elseRemoveBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.removeBranch(ifCard.id, branch.id);
    });
    elseActions.appendChild(elseRemoveBtn);
    elseCard.appendChild(elseActions);

    this.setupElseHeaderDropEvents(elseCard, branch.id);

    return elseCard;
  }

  private createBranchBodyElement(
    branch: Branch,
    depth: number,
    counter: { value: number }
  ): HTMLElement {
    const body = document.createElement("div");
    body.className = "nest";
    body.style.setProperty("--c", getCardColor("if"));
    body.setAttribute("data-branch-id", branch.id);
    body.dataset.nestType = "if";

    if (branch.cards.length > 0) {
      const childElements = this.renderCardList(
        branch.cards,
        depth + 1,
        counter
      );
      body.append(...childElements);
    }

    this.setupBranchDropEvents(body, branch.id);

    const addArea = document.createElement("div");
    addArea.className = "nest__add";
    addArea.textContent = "＋ 呪文を追加";
    if (this.activeContainerId === branch.id) {
      addArea.classList.add("nest__add--target");
    }
    addArea.addEventListener("click", (e) => {
      e.stopPropagation();
      this.setActiveContainerId(branch.id);
      this.opts.onBranchBodyAddClicked?.(branch.id);
      this.render();
    });
    body.appendChild(addArea);

    return body;
  }

  private createWhileBodyElement(
    branch: Branch,
    depth: number,
    counter: { value: number }
  ): HTMLElement {
    const body = document.createElement("div");
    body.className = "nest";
    body.style.setProperty("--c", getCardColor("while"));
    body.setAttribute("data-branch-id", branch.id);
    body.dataset.nestType = "while";

    if (branch.cards.length > 0) {
      const childElements = this.renderCardList(
        branch.cards,
        depth + 1,
        counter
      );
      body.append(...childElements);
    }

    this.setupBranchDropEvents(body, branch.id);

    const addArea = document.createElement("div");
    addArea.className = "nest__add";
    addArea.textContent = "＋ 呪文を追加";
    if (this.activeContainerId === branch.id) {
      addArea.classList.add("nest__add--target");
    }
    addArea.addEventListener("click", (e) => {
      e.stopPropagation();
      this.setActiveContainerId(branch.id);
      this.opts.onBranchBodyAddClicked?.(branch.id);
      this.render();
    });
    body.appendChild(addArea);

    return body;
  }

  private createAddBranchButtons(card: Card): HTMLElement {
    const row = document.createElement("div");
    row.className = "nest-else";
    row.style.setProperty("--c", getCardColor("if"));

    const elseBtn = document.createElement("button");
    elseBtn.className = "nest-else__btn";
    elseBtn.textContent = "+ それ以外は";
    elseBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const maxMP = this.opts.getMaxMP?.() ?? Infinity;
      this.addElseBranch(card, maxMP);
    });
    row.appendChild(elseBtn);

    return row;
  }

  private createCardElement(card: Card, displayNumber: number): HTMLElement {
    const element = document.createElement("div");
    element.className = `card-d card-d--${card.type}`;
    element.style.setProperty("--c", getCardColor(card.type));
    element.setAttribute("draggable", "true");
    element.setAttribute("data-card-id", card.id);
    element.setAttribute("role", "listitem");
    element.setAttribute("aria-label", `${displayNumber}番目: ${card.label}`);

    // ストライプ
    const stripe = document.createElement("div");
    stripe.className = "card-d__stripe";
    stripe.style.background = "var(--c)";
    element.appendChild(stripe);

    // カード番号（左側）
    const numberSpan = document.createElement("span");
    numberSpan.className = "card-d__number";
    numberSpan.textContent = String(displayNumber);
    element.appendChild(numberSpan);

    // アイコン（右側: CSS文字 / SVG / 着色丸）
    const icon = document.createElement("span");
    icon.className = "card-d__icon";
    icon.setAttribute("aria-hidden", "true");
    if (card.type === "forward") {
      icon.textContent = "↑";
    } else if (card.type === "if") {
      icon.textContent = "?";
    } else if (card.type === "addToOrb" || card.type === "multiplyOrb") {
      const dot = document.createElement("span");
      dot.className = `card-d__orb-dot${card.orbColor ? ` orb-color--${card.orbColor}` : ""}`;
      icon.appendChild(dot);
    } else if (this.opts.useIconSVG) {
      if (card.type === "while") {
        icon.innerHTML = getWhileIconSVG();
      } else if (card.type === "turn") {
        const direction = TURN_VALUE_TO_DIRECTION[card.value ?? 1];
        icon.innerHTML = getTurnDirectionSVG(direction);
      } else if (card.type === "call") {
        icon.innerHTML = getCallIconSVG();
      }
    }
    element.appendChild(icon);

    // カードラベル（タイプ別）
    if (card.type === "forward") {
      this.renderForwardLabel(element, card);
    } else if (card.type === "addToOrb" || card.type === "multiplyOrb") {
      this.renderOrbLabel(element, card);
    } else if (card.type === "if") {
      this.renderIfLabel(element, card);
    } else if (card.type === "while") {
      this.renderWhileLabel(element, card);
    } else if (card.type === "turn") {
      this.renderTurnLabel(element, card);
    } else if (card.type === "call") {
      this.renderCallLabel(element, card);
    } else {
      const labelSpan = document.createElement("span");
      labelSpan.className = "card-d__label";
      labelSpan.textContent = card.label;
      element.appendChild(labelSpan);
    }

    // コスト表示
    const costSpan = document.createElement("span");
    costSpan.className = "card-d__cost";

    if (card.type === "if") {
      costSpan.textContent = String(BRANCH_COST);
    } else if (card.type === "while") {
      costSpan.textContent = String(WHILE_BASE_COST);
    } else {
      costSpan.textContent = String(getCardCost(card));
    }

    element.appendChild(costSpan);

    // アクションボタン群（▲▼×）
    const actions = document.createElement("span");
    actions.className = "card-d__actions";

    const upBtn = document.createElement("button");
    upBtn.className = "card-d__btn";
    upBtn.setAttribute("aria-label", `${card.label}を上に移動`);
    upBtn.textContent = "▲";
    upBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.moveCardUp(card.id);
    });

    const downBtn = document.createElement("button");
    downBtn.className = "card-d__btn";
    downBtn.setAttribute("aria-label", `${card.label}を下に移動`);
    downBtn.textContent = "▼";
    downBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.moveCardDown(card.id);
    });

    const removeBtn = document.createElement("button");
    removeBtn.className = "card-d__btn card-d__btn--del";
    removeBtn.setAttribute("aria-label", `${card.label}を削除`);
    removeBtn.textContent = "×";
    removeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.removeCard(card.id);
    });

    actions.append(upBtn, downBtn, removeBtn);
    element.appendChild(actions);

    // ドラッグ&ドロップ
    this.setupDragEvents(element);

    return element;
  }

  // ─── カードラベル描画 ───

  private renderForwardLabel(element: HTMLElement, card: Card): void {
    const labelContainer = document.createElement("span");
    // オーブ参照時は ink-surge の filter が orb dot に干渉するためアニメーション停止
    labelContainer.className = card.orbColor
      ? "card-d__label card-d__label--orb"
      : "card-d__label";

    if (card.orbColor) {
      const indicator = this.createOrbIndicator(card.orbColor);
      labelContainer.appendChild(indicator);
    }

    const stepButton = this.createStepButton(card);
    labelContainer.appendChild(stepButton);

    labelContainer.appendChild(document.createTextNode("前進"));
    element.appendChild(labelContainer);
  }

  private renderOrbLabel(element: HTMLElement, card: Card): void {
    const labelContainer = document.createElement("span");
    labelContainer.className = "card-d__label";

    const orbButton = this.createOrbEditButton(card);
    labelContainer.appendChild(orbButton);

    element.appendChild(labelContainer);
  }

  private renderIfLabel(element: HTMLElement, card: Card): void {
    const branch = card.branches?.[0];
    const hasOrbCond = branch?.condition?.type === "orbComparison";
    const labelContainer = document.createElement("span");
    labelContainer.className = hasOrbCond
      ? "card-d__label card-d__label--orb"
      : "card-d__label";

    labelContainer.appendChild(document.createTextNode("もし "));

    const conditionBtn = this.createConditionButton(branch);
    labelContainer.appendChild(conditionBtn);

    labelContainer.appendChild(document.createTextNode(" なら"));
    element.appendChild(labelContainer);
  }

  private renderWhileLabel(element: HTMLElement, card: Card): void {
    const branch = card.branches?.[0];
    const hasOrbCond = branch?.condition?.type === "orbComparison";
    const labelContainer = document.createElement("span");
    labelContainer.className = hasOrbCond
      ? "card-d__label card-d__label--orb"
      : "card-d__label";

    labelContainer.appendChild(document.createTextNode("くりかえし："));

    const conditionBtn = this.createConditionButton(branch);
    labelContainer.appendChild(conditionBtn);

    element.appendChild(labelContainer);
  }

  private renderTurnLabel(element: HTMLElement, card: Card): void {
    const labelContainer = document.createElement("span");
    labelContainer.className = "card-d__label";

    const turnButton = this.createTurnButton(card);
    labelContainer.appendChild(turnButton);

    element.appendChild(labelContainer);
  }

  private renderCallLabel(element: HTMLElement, card: Card): void {
    const labelContainer = document.createElement("span");
    labelContainer.className = "card-d__label";

    const textSpan = document.createElement("span");
    textSpan.textContent = card.subbookId ? "よびだす：" : "よびだす";
    labelContainer.appendChild(textSpan);

    if (card.subbookId) {
      const subbookButton = this.createSubbookButton(card);
      labelContainer.appendChild(subbookButton);
    }

    element.appendChild(labelContainer);
  }

  // ─── ボタン生成 ───

  private createOrbIndicator(color: OrbColor): HTMLElement {
    const indicator = document.createElement("span");
    indicator.className = `card-d__orb-indicator orb-color--${color}`;
    indicator.setAttribute("aria-label", `${ORB_COLOR_LABELS[color]}オーブ`);
    return indicator;
  }

  private createStepButton(card: Card): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = "card-d__edit-btn";

    if (card.orbColor) {
      button.textContent = ORB_COLOR_LABELS[card.orbColor];
      button.setAttribute(
        "aria-label",
        `移動マス数を選択（現在: ${ORB_COLOR_LABELS[card.orbColor]}オーブ、コスト: ${FORWARD_ORB_COST}）`
      );
    } else {
      const currentValue = card.value ?? 1;
      const currentCost = getForwardCost(currentValue);
      button.textContent = `${currentValue}マス`;
      button.setAttribute(
        "aria-label",
        `移動マス数を選択（現在: ${currentValue}マス、コスト: ${currentCost}）`
      );
    }

    button.addEventListener("click", (e) => {
      e.stopPropagation();
      this.openStepSelector(card);
    });
    button.addEventListener("mousedown", (e) => e.stopPropagation());

    return button;
  }

  private createOrbEditButton(card: Card): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = "card-d__edit-btn";

    const colorLabel = card.orbColor ? ORB_COLOR_LABELS[card.orbColor] : "緑";
    const opLabel = card.type === "addToOrb" ? "足す" : "かける";
    button.textContent = `${colorLabel}に${card.value ?? 1}${opLabel}`;
    button.setAttribute(
      "aria-label",
      `${colorLabel}に${card.value ?? 1}${opLabel}を編集`
    );

    button.addEventListener("click", (e) => {
      e.stopPropagation();
      this.openOrbSelector(card);
    });
    button.addEventListener("mousedown", (e) => e.stopPropagation());

    return button;
  }

  private createTurnButton(card: Card): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = "card-d__edit-btn";

    const direction = TURN_VALUE_TO_DIRECTION[card.value ?? 1];
    button.textContent = TURN_DIRECTION_LABELS[direction];
    button.setAttribute(
      "aria-label",
      `方向を変更（現在: ${TURN_DIRECTION_LABELS[direction]}）`
    );

    button.addEventListener("click", (e) => {
      e.stopPropagation();
      this.openTurnSelector(card);
    });
    button.addEventListener("mousedown", (e) => e.stopPropagation());

    return button;
  }

  private createSubbookButton(card: Card): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = "card-d__edit-btn";

    const label = card.subbookId ? SUBBOOK_LABEL_SUFFIX[card.subbookId] : "?";
    button.textContent = `「サブブック${label}」`;
    button.setAttribute(
      "aria-label",
      `よびだすサブブックを変更（現在: サブブック${label}）`
    );

    button.addEventListener("click", (e) => {
      e.stopPropagation();
      this.openSubbookSelector(card);
    });
    button.addEventListener("mousedown", (e) => e.stopPropagation());

    return button;
  }

  private createConditionButton(branch?: Branch): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = "card-d__edit-btn";

    const condition = branch?.condition;
    const label = condition ? getConditionLabel(condition) : "条件なし";
    button.setAttribute("aria-label", `条件を変更（現在: ${label}）`);

    if (condition?.type === "orbComparison") {
      const orbDot = this.createOrbIndicator(condition.orbColor);
      orbDot.setAttribute("aria-hidden", "true");
      orbDot.removeAttribute("aria-label");
      button.appendChild(orbDot);
      button.appendChild(
        document.createTextNode(
          `${OPERATOR_LABELS[condition.operator]}${condition.value}`
        )
      );
    } else {
      button.textContent = label;
    }

    if (branch) {
      button.addEventListener("click", (e) => {
        e.stopPropagation();
        this.openConditionSelector(branch);
      });
      button.addEventListener("mousedown", (e) => e.stopPropagation());
    }

    return button;
  }

  // ─── モーダル ───

  private openStepSelector(card: Card): void {
    const modal = new StepSelectorModal();
    const availableMP = this.opts.getAvailableMP?.(card) ?? Infinity;

    modal.onSelect((selection: StepSelection) => {
      this.updateForwardCard(card.id, selection);
    });

    const current: StepSelection = card.orbColor
      ? { type: "orb", color: card.orbColor }
      : { type: "steps", value: card.value ?? 1 };

    modal.open(current, availableMP);
  }

  private openOrbSelector(card: Card): void {
    const modal = new OrbSelectorModal();
    const availableMP = this.opts.getAvailableMP?.(card) ?? Infinity;
    const currentOperator: OrbCardMode =
      card.type === "addToOrb" ? "add" : "multiply";

    modal.onSelect((selection) => {
      this.updateOrbCard(
        card.id,
        selection.operator,
        selection.color,
        selection.value
      );
    });

    modal.open(
      currentOperator,
      card.orbColor ?? "green",
      card.value ?? (card.type === "addToOrb" ? 1 : 2),
      availableMP
    );
  }

  private openTurnSelector(card: Card): void {
    if (!this.turnModal) {
      this.turnModal = new TurnSelectorModal();
    }
    const currentDirection = TURN_VALUE_TO_DIRECTION[card.value ?? 1];

    this.turnModal.onSelect((direction: TurnDirection) => {
      this.updateTurnCard(card.id, direction);
    });

    this.turnModal.open(currentDirection);
  }

  private openSubbookSelector(card: Card): void {
    if (!this.subbookModal) {
      this.subbookModal = new SubbookSelectorModal();
    }

    this.subbookModal.onSelect((subbookId: SubbookId) => {
      this.updateCallCard(card.id, subbookId);
    });

    this.subbookModal.open(this.subbookSlots);
  }

  private openConditionSelector(branch: Branch): void {
    const modal = new ConditionSelectorModal();
    modal.onSelect((condition: Condition) => {
      this.updateBranchCondition(branch.id, condition);
    });
    modal.open(branch.condition);
  }

  // ─── D&D ───

  private setupRootDropEvents(): void {
    const container = this.opts.rootDropContainer;

    container.addEventListener("dragover", (e) => {
      e.preventDefault();
      if (e.dataTransfer?.types.includes(this.opts.mimeType)) {
        e.dataTransfer.dropEffect = "move";
      } else if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }

      this.clearDropIndicators();
      if (this.cards.length > 0) {
        this.findInsertionIndex(container, e.clientY);
      }
    });

    container.addEventListener("dragleave", (e) => {
      if (!container.contains(e.relatedTarget as HTMLElement)) {
        this.clearDropIndicators();
      }
    });

    container.addEventListener("drop", (e) => {
      e.preventDefault();
      if (!e.dataTransfer) return;
      const draggedId = e.dataTransfer.getData(this.opts.mimeType);
      if (!draggedId) return;

      if (this.cards.length > 0) {
        const index = this.findInsertionIndex(container, e.clientY);
        this.moveCardToRoot(draggedId, index);
      } else {
        this.moveCardToRoot(draggedId);
      }
    });
  }

  private setupDragEvents(element: HTMLElement): void {
    element.addEventListener("dragstart", (e) => {
      if (!e.dataTransfer) return;
      const cardId = element.getAttribute("data-card-id");
      e.dataTransfer.setData(this.opts.mimeType, cardId || "");
      e.dataTransfer.effectAllowed = "move";

      const preview = element.cloneNode(true) as HTMLElement;
      preview.classList.add("card-d--drag-preview");
      preview.style.position = "absolute";
      preview.style.top = "-9999px";
      document.body.appendChild(preview);
      e.dataTransfer.setDragImage(
        preview,
        element.offsetWidth / 2,
        element.offsetHeight / 2
      );
      this.dragPreview = preview;

      element.classList.add("card-d--dragging");
      this.draggingCardId = cardId;
    });

    element.addEventListener("dragend", () => {
      element.classList.remove("card-d--dragging");
      this.draggingCardId = null;
      this.dragPreview?.remove();
      this.dragPreview = null;
      this.clearDropIndicators();
    });

    element.addEventListener("dragover", (e) => {
      // 内部カード移動のみ処理（パレットからのドラッグは container に委譲）
      if (!e.dataTransfer?.types.includes(this.opts.mimeType)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";

      this.clearDropIndicators();
      // if/while カードは論理末尾要素（ブランチボディ等）を含めて中点を計算する
      const topY = element.getBoundingClientRect().top;
      const logicalLastEl = this.getLogicalLastEl(element);
      const bottomY = logicalLastEl.getBoundingClientRect().bottom;
      const midY = (topY + bottomY) / 2;
      if (e.clientY < midY) {
        element.classList.add("card-d--drop-above");
      } else {
        logicalLastEl.classList.add("card-d--drop-below");
      }
    });

    element.addEventListener("dragleave", () => {
      element.classList.remove("card-d--drop-above", "card-d--drop-below");
    });

    element.addEventListener("drop", (e) => {
      // 内部カード移動のみ処理（パレットからのドロップは container に委譲）
      if (!e.dataTransfer?.types.includes(this.opts.mimeType)) return;
      e.preventDefault();
      e.stopPropagation();
      element.classList.remove("card-d--drop-above", "card-d--drop-below");

      const draggedId = e.dataTransfer.getData(this.opts.mimeType);
      const targetId = element.getAttribute("data-card-id");

      if (draggedId && targetId && draggedId !== targetId) {
        const rect = element.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const position = e.clientY < midY ? "before" : "after";
        this.moveCard(draggedId, targetId, position);
      }
    });
  }

  private setupElseHeaderDropEvents(
    elseHeader: HTMLElement,
    branchId: string
  ): void {
    // elseHeaderの次の兄弟(elseボディ .nest)を取得する
    // renderのたびにDOMが再生成されるため、実行時に毎回取得する
    const getElseBody = (): HTMLElement | null => {
      const next = elseHeader.nextElementSibling as HTMLElement | null;
      return next?.classList.contains("nest") ? next : null;
    };

    // elseボディの先頭にdrop indicatorを設定
    const setDropIndicator = () => {
      const body = getElseBody();
      if (!body) return;
      const firstCard = Array.from(body.children).find(
        (el) =>
          el.classList.contains("card-d") &&
          !el.classList.contains("card-d--else")
      ) as HTMLElement | undefined;
      if (firstCard) {
        firstCard.classList.add("card-d--drop-above");
      } else {
        const nestAdd = Array.from(body.children).find((el) =>
          el.classList.contains("nest__add")
        ) as HTMLElement | undefined;
        nestAdd?.classList.add("nest__add--target");
      }
    };

    elseHeader.addEventListener("dragover", (e) => {
      const types = Array.from(e.dataTransfer?.types ?? []);
      const isMine = types.includes(this.opts.mimeType);
      const isPalette = types.includes(PALETTE_MIME_TYPE);
      if (!isMine && !isPalette) return;

      e.preventDefault();
      e.stopPropagation();
      this.clearDropIndicators();

      if (isMine) {
        // 循環参照チェック: draggingCardIdがelseブランチの祖先なら禁止
        if (
          this.draggingCardId &&
          this.isBranchDescendantOf(branchId, this.draggingCardId)
        ) {
          if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
          getElseBody()?.classList.add("nest--forbidden");
          return;
        }
        if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
      } else {
        // パレットからの新規追加は循環参照なし
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      }
      setDropIndicator();
    });

    elseHeader.addEventListener("dragleave", (e) => {
      if (!elseHeader.contains(e.relatedTarget as HTMLElement)) {
        this.clearDropIndicators();
      }
    });

    elseHeader.addEventListener("drop", (e) => {
      const types = Array.from(e.dataTransfer?.types ?? []);
      const isMine = types.includes(this.opts.mimeType);
      const isPalette = types.includes(PALETTE_MIME_TYPE);
      if (!isMine && !isPalette) return;

      e.preventDefault();
      e.stopPropagation();
      this.clearDropIndicators();

      if (isPalette) {
        const cardType = e.dataTransfer?.getData(PALETTE_MIME_TYPE);
        if (cardType && cardType in CARD_DEFINITIONS) {
          this.setActiveContainerId(branchId);
          this.render();
          this.opts.onPaletteDropToBranch?.(branchId, cardType, undefined);
        }
      } else {
        const draggedId = e.dataTransfer?.getData(this.opts.mimeType);
        if (draggedId) {
          this.moveCardToBranch(draggedId, branchId, 0);
        }
      }
    });
  }

  private setupBranchDropEvents(body: HTMLElement, branchId: string): void {
    const setDropTarget = () => {
      const nestAdd = body.querySelector(".nest__add");
      if (nestAdd) nestAdd.classList.add("nest__add--target");
    };
    const clearDropTarget = () => {
      const nestAdd = body.querySelector(".nest__add");
      if (nestAdd) nestAdd.classList.remove("nest__add--target");
    };

    body.addEventListener("dragover", (e) => {
      if (e.dataTransfer?.types.includes(this.opts.mimeType)) {
        // 内部カード移動
        e.preventDefault();
        e.stopPropagation();

        if (
          this.draggingCardId &&
          this.isBranchDescendantOf(branchId, this.draggingCardId)
        ) {
          if (e.dataTransfer) e.dataTransfer.dropEffect = "none";
          this.clearDropIndicators();
          body.classList.add("nest--forbidden");
          return;
        }

        e.dataTransfer.dropEffect = "move";
        this.clearDropIndicators();

        const branch = this.findBranch(branchId);
        if (branch && branch.cards.length > 0) {
          this.findInsertionIndex(body, e.clientY);
        } else {
          setDropTarget();
        }
      } else if (e.dataTransfer?.types.includes(PALETTE_MIME_TYPE)) {
        // パレットからのドラッグ: ブランチにカードがあれば黄色い線で位置を示す
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        this.clearDropIndicators();
        const branch = this.findBranch(branchId);
        if (branch && branch.cards.length > 0) {
          this.findInsertionIndex(body, e.clientY);
        } else {
          setDropTarget();
        }
      }
    });

    body.addEventListener("dragleave", (e) => {
      if (!body.contains(e.relatedTarget as HTMLElement)) {
        clearDropTarget();
        body.classList.remove("nest--forbidden");
        body
          .querySelectorAll(".card-d--drop-above, .card-d--drop-below")
          .forEach((el) =>
            el.classList.remove("card-d--drop-above", "card-d--drop-below")
          );
      }
    });

    body.addEventListener("drop", (e) => {
      if (e.dataTransfer?.types.includes(PALETTE_MIME_TYPE)) {
        // パレットからのドロップ: ドロップ位置を挿入インデックスとして渡す
        e.preventDefault();
        e.stopPropagation();
        clearDropTarget();
        const cardType = e.dataTransfer.getData(PALETTE_MIME_TYPE);
        if (cardType && cardType in CARD_DEFINITIONS) {
          const branch = this.findBranch(branchId);
          const insertionIndex =
            branch && branch.cards.length > 0
              ? this.findInsertionIndex(body, e.clientY)
              : undefined;
          this.setActiveContainerId(branchId);
          this.render();
          this.opts.onPaletteDropToBranch?.(branchId, cardType, insertionIndex);
        }
      } else if (e.dataTransfer?.types.includes(this.opts.mimeType)) {
        // 内部カード移動
        e.preventDefault();
        e.stopPropagation();
        clearDropTarget();

        const draggedId = e.dataTransfer.getData(this.opts.mimeType);
        if (!draggedId) return;

        const branch = this.findBranch(branchId);
        if (branch && branch.cards.length > 0) {
          const index = this.findInsertionIndex(body, e.clientY);
          this.moveCardToBranch(draggedId, branchId, index);
        } else {
          this.moveCardToBranch(draggedId, branchId);
        }
      }
    });
  }

  clearDropIndicators(): void {
    this.opts.rootDropContainer
      .querySelectorAll(
        ".card-d--drop-above, .card-d--drop-below, .nest__add--target, .nest--forbidden"
      )
      .forEach((el) =>
        el.classList.remove(
          "card-d--drop-above",
          "card-d--drop-below",
          "nest__add--target",
          "nest--forbidden"
        )
      );
  }

  /**
   * 要素が if/while のブランチグループ要素かどうか判定する。
   * drop-tail 等を含まないよう既知クラスのみを対象にする。
   */
  private isBranchGroupEl(el: HTMLElement): boolean {
    if (el.classList.contains("nest--root")) return false;
    return ["nest", "card-d--else", "nest-else"].some((cls) =>
      el.classList.contains(cls)
    );
  }

  /**
   * if/while カードの「論理的な末尾要素」を返す。
   * ブランチボディ・ヘッダー等の兄弟要素を辿り、非グループ要素で停止する。
   */
  private getLogicalLastEl(cardEl: HTMLElement): HTMLElement {
    let el = cardEl.nextElementSibling as HTMLElement | null;
    let lastEl = cardEl;
    while (el && this.isBranchGroupEl(el)) {
      lastEl = el;
      el = el.nextElementSibling as HTMLElement | null;
    }
    return lastEl;
  }

  findInsertionIndex(containerEl: HTMLElement, clientY: number): number {
    const cardEls = Array.from(containerEl.children).filter(
      (el) =>
        el.classList.contains("card-d") &&
        !el.classList.contains("card-d--else")
    ) as HTMLElement[];

    if (cardEls.length === 0) return 0;

    for (let i = 0; i < cardEls.length; i++) {
      const topY = cardEls[i].getBoundingClientRect().top;
      const lastEl = this.getLogicalLastEl(cardEls[i]);
      const bottomY = lastEl.getBoundingClientRect().bottom;
      if (clientY < (topY + bottomY) / 2) {
        cardEls[i].classList.add("card-d--drop-above");
        return i;
      }
    }

    // 末尾: nest__add--target を使う（末尾に黄色い線を出さない）
    // branch body (nest) は直接子の nest__add を、root container は nest--root 配下を探す
    const nestAdd = containerEl.classList.contains("nest")
      ? (Array.from(containerEl.children).find((el) =>
          el.classList.contains("nest__add")
        ) as HTMLElement | undefined)
      : (containerEl.querySelector(
          ".nest--root > .nest__add"
        ) as HTMLElement | null);
    if (nestAdd) {
      nestAdd.classList.add("nest__add--target");
    } else {
      // nest__add がない場合のフォールバック（手動DOM構築のテスト等）
      const lastCard = cardEls[cardEls.length - 1];
      const lastLogicalEl = this.getLogicalLastEl(lastCard);
      lastLogicalEl.classList.add("card-d--drop-below");
    }
    return cardEls.length;
  }
}
